/**
 * PowerPoint Template Engine
 *
 * Parses .pptx files as ZIP archives (Open XML format) and manipulates
 * the XML to clone slide layouts and inject artefact content into
 * placeholder shapes.
 *
 * PPTX structure:
 *   ppt/presentation.xml      — slide list and relationships
 *   ppt/slides/slide1.xml     — individual slides
 *   ppt/slides/_rels/slide1.xml.rels — slide relationships
 *   ppt/slideLayouts/         — layout definitions
 *   ppt/slideMasters/         — master slide definitions
 *   [Content_Types].xml       — content type registry
 */

import type { Artefact } from "@/types/artefacts";

import PizZip from "pizzip";

import { logger } from "@/telemetry";
import { ArtefactType } from "@/types/artefacts";
import { formatLongDate } from "@/utils/dateFormatting";
import { stripMarkdown } from "@/utils/templateEngine/templateUtils";

/** Minimal image data needed to embed a captured HTML artefact as an image */
export interface PptxImageData {
  /** Binary PNG data */
  imageData: Uint8Array;
  /** Original capture width in pixels */
  width: number;
  /** Original capture height in pixels */
  height: number;
}

export interface PptxTemplateData {
  title?: string;
  date?: string;
  author?: string;
  brand?: string;
  /** Font family for injected text (from Export Font setting). Falls back to theme font. */
  fontFamily?: string;
  artefacts: Array<{
    id: string;
    title: string;
    content: string;
    type: ArtefactType;
  }>;
  /** Pre-captured images for HTML artefacts, keyed by artefact ID */
  imageMap?: Map<string, PptxImageData>;
}

// ============================================================================
// TABLE CONSTANTS — match the non-template export (markdownToPptx.ts)
// ============================================================================
const MAX_TABLE_ROWS_PER_SLIDE = 6;

/** Row height in EMU (approx 0.45 inch per row) — 914400 EMU = 1 inch */
const TABLE_ROW_HEIGHT_EMU = Math.round(0.45 * 914400);

/** Font sizes in hundredths of a point */
const TABLE_HEADER_FONT_SIZE = 1200; // 12pt
const TABLE_BODY_FONT_SIZE = 1100; // 11pt

// ============================================================================
// OOXML PATTERNS
// ============================================================================
// Patterns live here rather than inline because of two defects in lizard 1.24,
// which produces the code health report. It drops the token that follows a
// regex literal, so `.match(/.../)` loses its closing bracket, and it reads a
// quote inside a regex literal as the start of a string that runs on to the next
// matching quote anywhere in the file. Either way every function after the
// pattern is measured as one. A pattern containing a quote is therefore built
// with `new RegExp` from an ordinary string.

/** Captures the `type` attribute of a slide placeholder (`<p:ph type="body"/>`). */
const PLACEHOLDER_TYPE_PATTERN = new RegExp('<p:ph[^>]*type="([^"]*)"[^>]*/>');

/** Captures the relationship id of each `<p:sldId>` in presentation.xml. */
const SLIDE_REL_ID_PATTERN = new RegExp('<p:sldId[^>]*r:id="([^"]+)"[^>]*/>', "g");

/** Captures the number in a slide part name (`slide12.xml`). */
const SLIDE_NUMBER_PATTERN = /slide(\d+)\.xml$/;

/** Captures the number of each `Id="rIdN"` in a relationships part. */
const REL_ID_NUMBER_PATTERN = new RegExp('Id="rId(\\d+)"', "g");

/** Captures a shape's `<a:off x y>` offset and `<a:ext cx cy>` extent. */
const SHAPE_OFFSET_PATTERN = new RegExp('<a:off\\s+x="(\\d+)"\\s+y="(\\d+)"');
const SHAPE_EXTENT_PATTERN = new RegExp('<a:ext\\s+cx="(\\d+)"\\s+cy="(\\d+)"');

/** Any character that must be escaped in XML text. */
const XML_SPECIAL_CHARACTER = new RegExp(`[&<>"']`, "g");

/**
 * Build the OOXML font elements for run properties.
 * Uses an explicit font family when provided (from Export Font setting),
 * otherwise falls back to theme font references (+mj-lt / +mn-lt).
 */
function buildFontXml(fontFamily?: string, isTitle?: boolean): string {
  const latin = fontFamily || (isTitle ? "+mj-lt" : "+mn-lt");
  const cs = fontFamily || (isTitle ? "+mj-cs" : "+mn-cs");

  return `<a:latin typeface="${escapeXml(latin)}"/><a:cs typeface="${escapeXml(cs)}"/>`;
}

/** Parse a markdown table string into header cells and body rows */
function parseMarkdownTable(markdown: string): { headers: string[]; rows: string[][] } {
  const lines = markdown
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.startsWith("|"));

  if (lines.length < 2) return { headers: [], rows: [] };

  const splitRow = (line: string): string[] =>
    line
      .replace(/^\|/, "")
      .replace(/\|$/, "")
      .split("|")
      .map((c) => c.trim());

  const headers = splitRow(lines[0]);

  // Skip separator line (index 1)
  const rows = lines.slice(2).map(splitRow);

  return { headers, rows };
}

/**
 * Build an OOXML `<a:tbl>` element from parsed table data.
 * This produces a native, editable PowerPoint table.
 */
function buildTableXml(
  headers: string[],
  bodyRows: string[],
  colCount: number,
  bounds: PlaceholderBounds,
  fontFamily?: string,
): string {
  const colWidth = Math.round(bounds.cx / colCount);
  const fontXml = buildFontXml(fontFamily, false);

  const gridCols = Array(colCount)
    .fill(0)
    .map(() => `<a:gridCol w="${colWidth}"/>`)
    .join("");

  const buildCell = (text: string, isHeader: boolean): string => {
    const fontSize = isHeader ? TABLE_HEADER_FONT_SIZE : TABLE_BODY_FONT_SIZE;
    const bold = isHeader ? ' b="1"' : "";
    const fontColour = isHeader ? "FFFFFF" : "363636";
    const fill = isHeader
      ? '<a:solidFill><a:srgbClr val="28326E"/></a:solidFill>'
      : '<a:solidFill><a:srgbClr val="FFFFFF"/></a:solidFill>';

    return `<a:tc>
  <a:txBody><a:bodyPr/><a:lstStyle/>
    <a:p><a:r><a:rPr lang="en-GB" sz="${fontSize}"${bold} dirty="0"><a:solidFill><a:srgbClr val="${fontColour}"/></a:solidFill>${fontXml}</a:rPr><a:t>${escapeXml(text)}</a:t></a:r></a:p>
  </a:txBody>
  <a:tcPr>${fill}<a:lnL w="12700"><a:solidFill><a:srgbClr val="CCCCCC"/></a:solidFill></a:lnL><a:lnR w="12700"><a:solidFill><a:srgbClr val="CCCCCC"/></a:solidFill></a:lnR><a:lnT w="12700"><a:solidFill><a:srgbClr val="CCCCCC"/></a:solidFill></a:lnT><a:lnB w="12700"><a:solidFill><a:srgbClr val="CCCCCC"/></a:solidFill></a:lnB></a:tcPr>
</a:tc>`;
  };

  const buildRow = (cells: string[], isHeader: boolean): string => {
    const cellsXml = cells.map((c) => buildCell(c, isHeader)).join("");

    return `<a:tr h="${TABLE_ROW_HEIGHT_EMU}">${cellsXml}</a:tr>`;
  };

  // Build header row(s)
  const headerRowXml = buildRow(headers, true);

  // Build body rows — bodyRows here is a flat array of cell values
  // We need to chunk them by colCount
  const dataRows: string[][] = [];

  for (let i = 0; i < bodyRows.length; i += colCount) {
    dataRows.push(bodyRows.slice(i, i + colCount));
  }

  const bodyRowsXml = dataRows.map((row) => buildRow(row, false)).join("");

  return `<a:tbl>
  <a:tblPr firstRow="1" bandRow="1"><a:tblStyle val="{5C22544A-7EE6-4342-B048-85BDC9FD1C3A}"/></a:tblPr>
  <a:tblGrid>${gridCols}</a:tblGrid>
  ${headerRowXml}${bodyRowsXml}
</a:tbl>`;
}

/**
 * Build a `<p:graphicFrame>` wrapping an OOXML table.
 * This replaces a body placeholder with a native table.
 */
function buildGraphicFrameXml(tableXml: string, bounds: PlaceholderBounds): string {
  return `<p:graphicFrame>
  <p:nvGraphicFramePr>
    <p:cNvPr id="1001" name="Table"/>
    <p:cNvGraphicFramePr><a:graphicFrameLocks noGrp="1"/></p:cNvGraphicFramePr>
    <p:nvPr/>
  </p:nvGraphicFramePr>
  <p:xfrm>
    <a:off x="${bounds.x}" y="${bounds.y}"/>
    <a:ext cx="${bounds.cx}" cy="${bounds.cy}"/>
  </p:xfrm>
  <a:graphic>
    <a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/table">
      ${tableXml}
    </a:graphicData>
  </a:graphic>
</p:graphicFrame>`;
}

/**
 * Replace the body/subtitle placeholder in a slide with a native OOXML table.
 */
function replaceBodyPlaceholderWithTable(
  slideXml: string,
  headers: string[],
  bodyRows: string[][],
  fontFamily?: string,
): string {
  let replaced = false;

  return slideXml.replace(/<p:sp>[\s\S]*?<\/p:sp>/g, (spBlock) => {
    if (replaced) return spBlock;

    const phMatch = spBlock.match(PLACEHOLDER_TYPE_PATTERN);

    if (!phMatch) return spBlock;

    const phType = phMatch[1];

    if (phType !== "body" && phType !== "subTitle") return spBlock;

    replaced = true;
    const bounds = getPlaceholderBounds(spBlock) || DEFAULT_BODY_BOUNDS;
    const colCount = headers.length;
    const flatCells = bodyRows.flat();
    const tableXml = buildTableXml(headers, flatCells, colCount, bounds, fontFamily);

    return buildGraphicFrameXml(tableXml, bounds);
  });
}

const XML_ESCAPES: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&apos;",
};

/** Escape XML special characters in text values */
function escapeXml(text: string): string {
  return text.replace(XML_SPECIAL_CHARACTER, (char) => XML_ESCAPES[char]);
}

/**
 * Build an OOXML text body (`<p:txBody>`) with the given text.
 * Splits on newlines to create separate `<a:p>` paragraphs.
 */
function buildTextBody(
  text: string,
  fontSize?: number,
  isTitle?: boolean,
  fontFamily?: string,
): string {
  const lines = text.split("\n").filter((line) => line.trim() !== "");
  const fontAttr = fontSize ? ` sz="${fontSize * 100}"` : "";
  const fontXml = buildFontXml(fontFamily, isTitle);

  const paragraphs = lines
    .map(
      (line) =>
        `<a:p><a:r><a:rPr lang="en-GB" dirty="0"${fontAttr}>${fontXml}</a:rPr><a:t>${escapeXml(line)}</a:t></a:r></a:p>`,
    )
    .join("");

  return `<p:txBody><a:bodyPr/><a:lstStyle/>${paragraphs || '<a:p><a:endParaRPr lang="en-GB"/></a:p>'}</p:txBody>`;
}

/**
 * Inject text into PPTX placeholder shapes identified by `<p:ph>` type attributes.
 *
 * Real PPTX templates have empty `<p:txBody>` elements inside `<p:sp>` shapes.
 * The "Click to add text" prompt comes from the slide layout, not the slide XML.
 *
 * Strategy: extract each `<p:sp>...</p:sp>` block, check its `<p:ph>` type,
 * and replace the `<p:txBody>` with actual content.
 */
function injectTextIntoSlide(
  xml: string,
  replacements: {
    title?: string;
    content?: string;
    extraReplacements?: Record<string, string>;
    fontFamily?: string;
  },
): string {
  let result = xml;

  // Strategy 1: Process each <p:sp> block individually
  result = result.replace(/<p:sp>[\s\S]*?<\/p:sp>/g, (spBlock) => {
    // Check what type of placeholder this shape is
    const phMatch = spBlock.match(PLACEHOLDER_TYPE_PATTERN);

    if (!phMatch) return spBlock; // Not a placeholder shape, leave as-is

    const phType = phMatch[1];

    // Title placeholder → inject title
    if ((phType === "title" || phType === "ctrTitle") && replacements.title) {
      const newBody = buildTextBody(replacements.title, 28, true, replacements.fontFamily);

      return spBlock.replace(/<p:txBody>[\s\S]*?<\/p:txBody>/, newBody);
    }

    // Body/subtitle placeholder → inject content
    if ((phType === "body" || phType === "subTitle") && replacements.content) {
      const newBody = buildTextBody(replacements.content, 14, false, replacements.fontFamily);

      return spBlock.replace(/<p:txBody>[\s\S]*?<\/p:txBody>/, newBody);
    }

    // Footer placeholder → replace known tokens in existing text
    if (phType === "ftr" && replacements.extraReplacements) {
      let updated = spBlock;

      for (const [token, value] of Object.entries(replacements.extraReplacements)) {
        // Replace within <a:t> elements
        const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const regex = new RegExp(`(<a:t[^>]*>)([^<]*${escaped}[^<]*)(</a:t>)`, "g");

        updated = updated.replace(regex, (_m, open, text, close) => {
          return `${open}${text.replace(token, escapeXml(value))}${close}`;
        });
      }

      return updated;
    }

    return spBlock;
  });

  // Strategy 2: Direct PLACEHOLDER replacement in any remaining <a:t> elements
  const allReplacements: Record<string, string> = {
    ...replacements.extraReplacements,
  };

  if (replacements.title) allReplacements["TITLE"] = replacements.title;
  if (replacements.content) allReplacements["CONTENT"] = replacements.content;

  for (const [placeholder, value] of Object.entries(allReplacements)) {
    const escapedValue = escapeXml(value);

    for (const pattern of [`{{${placeholder}}}`, `{${placeholder}}`]) {
      const escaped = pattern.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const regex = new RegExp(`(<a:t[^>]*>)([^<]*${escaped}[^<]*)(</a:t>)`, "g");

      result = result.replace(regex, (_match, openTag, textContent, closeTag) => {
        return `${openTag}${textContent.replace(pattern, escapedValue)}${closeTag}`;
      });
    }
  }

  // Strategy 3: Literal exact-match replacement for bare placeholder words.
  // Many real-world templates use plain text like AUTHOR / DATE / TITLE inside
  // placeholder text frames (no curly braces). Only replace when the entire
  // <a:t> element content equals the token (optional surrounding whitespace),
  // so we never clobber legitimate content that happens to contain the word.
  for (const [token, value] of Object.entries(allReplacements)) {
    if (!value) continue;
    const escapedToken = token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(<a:t[^>]*>)\\s*${escapedToken}\\s*(</a:t>)`, "g");

    result = result.replace(regex, (_match, openTag, closeTag) => {
      return `${openTag}${escapeXml(value)}${closeTag}`;
    });
  }

  return result;
}

/**
 * Get the list of slide filenames from presentation.xml.
 * Returns an ordered array of relationship IDs.
 */
function getSlideRelIds(presentationXml: string): string[] {
  const matches = [...presentationXml.matchAll(SLIDE_REL_ID_PATTERN)];

  return matches.map((m) => m[1]);
}

/**
 * Get slide filename from a relationship ID by reading presentation.xml.rels.
 */
function getSlideFilenameFromRel(relsXml: string, relId: string): string | null {
  const regex = new RegExp(`<Relationship[^>]*Id="${relId}"[^>]*Target="([^"]+)"[^>]*/>`);
  const match = relsXml.match(regex);

  return match ? match[1] : null;
}

/**
 * Find the highest slide number currently in the zip.
 */
function getMaxSlideNumber(zip: PizZip): number {
  let max = 0;

  const slideFiles = zip.file(/^ppt\/slides\/slide\d+\.xml$/);

  for (const file of slideFiles) {
    const match = file.name.match(SLIDE_NUMBER_PATTERN);

    if (match) {
      const num = parseInt(match[1], 10);

      if (num > max) max = num;
    }
  }

  return max;
}

/**
 * Find the highest relationship ID number in the presentation rels.
 */
function getMaxRelId(relsXml: string): number {
  const matches = [...relsXml.matchAll(REL_ID_NUMBER_PATTERN)];
  let max = 0;

  for (const m of matches) {
    const num = parseInt(m[1], 10);

    if (num > max) max = num;
  }

  return max;
}

/**
 * Detect which slide in the template is the "content" slide
 * (i.e. not the title/cover slide). Heuristic: the second slide,
 * or if only one slide exists, use that one.
 */
function findContentSlideIndex(slideCount: number): number {
  return slideCount > 1 ? 1 : 0; // 0-indexed; prefer second slide
}

// EMU (English Metric Units) constants for OOXML positioning
// 1 inch = 914400 EMU
const EMU_PER_INCH = 914400;

/** Position and size in EMU */
interface PlaceholderBounds {
  x: number;
  y: number;
  cx: number;
  cy: number;
}

/** Default body placeholder bounds for a standard 10"×7.5" slide */
const DEFAULT_BODY_BOUNDS: PlaceholderBounds = {
  x: Math.round(0.5 * EMU_PER_INCH), // 0.5" left margin
  y: Math.round(1.75 * EMU_PER_INCH), // Below title area
  cx: Math.round(9 * EMU_PER_INCH), // 9" wide
  cy: Math.round(5 * EMU_PER_INCH), // 5" tall
};

/**
 * Extract position bounds from a `<p:sp>` XML block.
 * Returns null if the shape has no explicit transform (inherits from layout).
 */
function getPlaceholderBounds(spBlock: string): PlaceholderBounds | null {
  const offMatch = spBlock.match(SHAPE_OFFSET_PATTERN);
  const extMatch = spBlock.match(SHAPE_EXTENT_PATTERN);

  if (!offMatch || !extMatch) return null;

  return {
    x: parseInt(offMatch[1], 10),
    y: parseInt(offMatch[2], 10),
    cx: parseInt(extMatch[1], 10),
    cy: parseInt(extMatch[2], 10),
  };
}

/**
 * Build OOXML `<p:pic>` element for an embedded image.
 * The image is centred within the given bounds while preserving aspect ratio.
 */
function buildPicXml(
  relId: string,
  bounds: PlaceholderBounds,
  imageWidthPx: number,
  imageHeightPx: number,
): string {
  const aspectRatio = imageWidthPx / imageHeightPx;
  const boundsRatio = bounds.cx / bounds.cy;

  let finalCx: number;
  let finalCy: number;

  if (aspectRatio > boundsRatio) {
    // Wider than bounds — constrain by width
    finalCx = bounds.cx;
    finalCy = Math.round(bounds.cx / aspectRatio);
  } else {
    // Taller than bounds — constrain by height
    finalCy = bounds.cy;
    finalCx = Math.round(bounds.cy * aspectRatio);
  }

  // Centre within the placeholder area
  const offsetX = bounds.x + Math.round((bounds.cx - finalCx) / 2);
  const offsetY = bounds.y + Math.round((bounds.cy - finalCy) / 2);

  return `<p:pic>
  <p:nvPicPr>
    <p:cNvPr id="1001" name="Chart Image"/>
    <p:cNvPicPr><a:picLocks noChangeAspect="1"/></p:cNvPicPr>
    <p:nvPr/>
  </p:nvPicPr>
  <p:blipFill>
    <a:blip r:embed="${relId}"/>
    <a:stretch><a:fillRect/></a:stretch>
  </p:blipFill>
  <p:spPr>
    <a:xfrm>
      <a:off x="${offsetX}" y="${offsetY}"/>
      <a:ext cx="${finalCx}" cy="${finalCy}"/>
    </a:xfrm>
    <a:prstGeom prst="rect"><a:avLst/></a:prstGeom>
  </p:spPr>
</p:pic>`;
}

/**
 * Replace the body/subtitle placeholder in a slide with a `<p:pic>` image element.
 * Falls back to default positioning if the placeholder has no explicit bounds.
 */
function replaceBodyPlaceholderWithImage(
  slideXml: string,
  imageRelId: string,
  imageWidthPx: number,
  imageHeightPx: number,
): string {
  let replaced = false;

  const result = slideXml.replace(/<p:sp>[\s\S]*?<\/p:sp>/g, (spBlock) => {
    if (replaced) return spBlock; // Only replace first body placeholder

    const phMatch = spBlock.match(PLACEHOLDER_TYPE_PATTERN);

    if (!phMatch) return spBlock;

    const phType = phMatch[1];

    if (phType !== "body" && phType !== "subTitle") return spBlock;

    replaced = true;
    const bounds = getPlaceholderBounds(spBlock) || DEFAULT_BODY_BOUNDS;

    return buildPicXml(imageRelId, bounds, imageWidthPx, imageHeightPx);
  });

  return result;
}

// ============================================================================
// SLIDE GENERATION
// ============================================================================

const SLIDE_CONTENT_TYPE = "application/vnd.openxmlformats-officedocument.presentationml.slide+xml";
const PRESENTATION_MAIN_CONTENT_TYPE =
  "application/vnd.openxmlformats-officedocument.presentationml.presentation.main+xml";
const SLIDE_RELATIONSHIP_TYPE =
  "http://schemas.openxmlformats.org/officeDocument/2006/relationships/slide";
const IMAGE_RELATIONSHIP_TYPE =
  "http://schemas.openxmlformats.org/officeDocument/2006/relationships/image";
const DRAWINGML_NAMESPACE = 'xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"';
const RELATIONSHIPS_NAMESPACE =
  'xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"';
const EMPTY_SLIDE_RELS = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"/>`;

/** Relationship id of the chart picture inside its own slide's rels part. */
const IMAGE_REL_ID = "rIdImg1";

/** The first `<p:sldId>` id OOXML allows; new slides are numbered above it. */
const MIN_SLIDE_ID = 256;

/** Captures the `id` attribute of each `<p:sldId>` in presentation.xml. */
const SLIDE_ID_PATTERN = new RegExp('<p:sldId[^>]*id="(\\d+)"[^>]*/>', "g");

/** The last `<p:sldId>` together with the list's closing tag. */
const LAST_SLIDE_ID_PATTERN = /<p:sldId[^>]*\/>\s*<\/p:sldIdLst>/;

type TemplateArtefact = PptxTemplateData["artefacts"][number];

interface SlideRef {
  num: number;
  relId: string;
}

interface AllocatedSlide extends SlideRef {
  filename: string;
}

/** The content slide every generated slide is cloned from. */
interface ContentSlideTemplate {
  xml: string;
  rels: string | undefined;
  fontFamily: string | undefined;
  footerReplacements: Record<string, string>;
}

/** Package state that slide generation reads and advances. */
interface DeckBuilder {
  zip: PizZip;
  contentTypesXml: string;
  presentationRels: string;
  maxSlideNum: number;
  maxRelId: number;
  maxSldId: number;
  newSlides: SlideRef[];
}

/** Resolve every slide in presentation.xml to its part name, in order. */
function readSlideFilenames(
  presentationXml: string,
  presentationRels: string,
): { slideRelIds: string[]; slideFilenames: string[] } {
  const slideRelIds = getSlideRelIds(presentationXml);
  const slideFilenames: string[] = [];

  for (const relId of slideRelIds) {
    const filename = getSlideFilenameFromRel(presentationRels, relId);

    if (filename) {
      slideFilenames.push(filename.startsWith("/") ? filename.slice(1) : filename);
    }
  }

  return { slideRelIds, slideFilenames };
}

function formatDeckDate(data: PptxTemplateData): string {
  return data.date || (formatLongDate(new Date()) ?? "");
}

/** Replace placeholders in the title slide, if the template has one. */
function fillTitleSlide(zip: PizZip, titleSlideFile: string, data: PptxTemplateData): void {
  const titleSlideXml = zip.file(titleSlideFile)?.asText();

  if (!titleSlideXml) return;

  // For the title slide we deliberately omit `content` so the body
  // placeholder isn't clobbered as a whole — that would wipe DATE when
  // we only want to replace AUTHOR. Strategy 3 (literal token match)
  // inside `injectTextIntoSlide` handles AUTHOR and DATE individually.
  const updatedTitleSlide = injectTextIntoSlide(titleSlideXml, {
    title: data.title || "",
    fontFamily: data.fontFamily,
    extraReplacements: {
      TITLE: data.title || "",
      DATE: formatDeckDate(data),
      AUTHOR: data.author || "",
      BRAND: data.brand || "",
      "Insert document title": data.title || "",
      "Insert date": formatDeckDate(data),
    },
  });

  zip.file(titleSlideFile, updatedTitleSlide);
}

/** Common footer replacements applied to every cloned content slide */
function buildFooterReplacements(data: PptxTemplateData): Record<string, string> {
  return {
    TITLE: data.title || "",
    DATE: formatDeckDate(data),
    AUTHOR: data.author || "",
    BRAND: data.brand || "",
  };
}

/**
 * Convert template content types (.potx/.pptm/.potm) to presentation (.pptx)
 * so the output file opens correctly in PowerPoint
 */
function toPresentationContentTypes(contentTypesXml: string): string {
  return contentTypesXml
    .replace(
      /application\/vnd\.openxmlformats-officedocument\.presentationml\.template\.main\+xml/g,
      PRESENTATION_MAIN_CONTENT_TYPE,
    )
    .replace(
      /application\/vnd\.ms-powerpoint\.presentation\.macroEnabled\.main\+xml/g,
      PRESENTATION_MAIN_CONTENT_TYPE,
    )
    .replace(
      /application\/vnd\.ms-powerpoint\.template\.macroEnabled\.main\+xml/g,
      PRESENTATION_MAIN_CONTENT_TYPE,
    );
}

/** Ensure PNG content type is registered (needed for image embedding) */
function ensurePngContentType(contentTypesXml: string): string {
  if (contentTypesXml.includes('Extension="png"')) return contentTypesXml;

  return contentTypesXml.replace(
    "</Types>",
    '<Default Extension="png" ContentType="image/png"/></Types>',
  );
}

/** The highest `<p:sldId>` id in presentation.xml, never below the OOXML minimum. */
function getMaxSlideId(presentationXml: string): number {
  let maxSldId = MIN_SLIDE_ID;

  for (const m of presentationXml.matchAll(SLIDE_ID_PATTERN)) {
    const num = parseInt(m[1], 10);

    if (num > maxSldId) maxSldId = num;
  }

  return maxSldId;
}

/** Declare a namespace on the slide root unless the slide already declares that prefix. */
function ensureSlideNamespace(slideXml: string, prefix: string, declaration: string): string {
  if (slideXml.includes(`${prefix}=`)) return slideXml;

  return slideXml.replace("<p:sld", `<p:sld ${declaration}`);
}

/** Reserve the next slide number, relationship id and slide id. */
function allocateSlide(deck: DeckBuilder): AllocatedSlide {
  deck.maxSlideNum++;
  deck.maxRelId++;
  deck.maxSldId++;

  return {
    num: deck.maxSlideNum,
    relId: `rId${deck.maxRelId}`,
    filename: `slide${deck.maxSlideNum}.xml`,
  };
}

/**
 * Write a slide part and its rels, register it in [Content_Types].xml and
 * presentation.xml.rels, and queue it for the slide id list.
 */
function writeSlide(
  deck: DeckBuilder,
  slide: AllocatedSlide,
  slideXml: string,
  slideRels: string | undefined,
): void {
  deck.zip.file(`ppt/slides/${slide.filename}`, slideXml);

  if (slideRels) {
    deck.zip.file(`ppt/slides/_rels/${slide.filename}.rels`, slideRels);
  }

  if (!deck.contentTypesXml.includes(`/ppt/slides/${slide.filename}`)) {
    deck.contentTypesXml = deck.contentTypesXml.replace(
      "</Types>",
      `<Override PartName="/ppt/slides/${slide.filename}" ContentType="${SLIDE_CONTENT_TYPE}"/></Types>`,
    );
  }

  deck.presentationRels = deck.presentationRels.replace(
    "</Relationships>",
    `<Relationship Id="${slide.relId}" Type="${SLIDE_RELATIONSHIP_TYPE}" Target="slides/${slide.filename}"/></Relationships>`,
  );

  deck.newSlides.push({ num: slide.num, relId: slide.relId });
}

/** Clone the content slide with only its title (and footer) filled in. */
function renderTitledSlide(template: ContentSlideTemplate, title: string): string {
  return injectTextIntoSlide(template.xml, {
    title,
    fontFamily: template.fontFamily,
    extraReplacements: {
      ...template.footerReplacements,
      TITLE: title,
      "Slide Title": title,
      "Click to add title": title,
    },
  });
}

/** Clone the content slide with a title and a plain-text body. */
function renderTextSlide(template: ContentSlideTemplate, title: string, content: string): string {
  return injectTextIntoSlide(template.xml, {
    title,
    content,
    fontFamily: template.fontFamily,
    extraReplacements: {
      ...template.footerReplacements,
      TITLE: title,
      CONTENT: content,
      "Slide Title": title,
      "Click to add title": title,
      "Click to add text": content,
    },
  });
}

/** Text artefact, or a TABLE artefact that is not a valid markdown table. */
function addTextSlide(
  deck: DeckBuilder,
  template: ContentSlideTemplate,
  artefact: TemplateArtefact,
): void {
  const slide = allocateSlide(deck);
  const content =
    artefact.type === ArtefactType.CODE ? artefact.content : stripMarkdown(artefact.content);

  writeSlide(deck, slide, renderTextSlide(template, artefact.title, content), template.rels || "");
}

/**
 * A native OOXML table, split across as many slides as it needs. Every slide
 * after the first is titled "(continued)" and repeats the header row.
 */
function addTableSlides(
  deck: DeckBuilder,
  template: ContentSlideTemplate,
  title: string,
  headers: string[],
  bodyRows: string[][],
): void {
  const slideCount = Math.max(1, Math.ceil(bodyRows.length / MAX_TABLE_ROWS_PER_SLIDE));

  for (let index = 0; index < slideCount; index++) {
    const slide = allocateSlide(deck);
    const start = index * MAX_TABLE_ROWS_PER_SLIDE;
    const chunk = bodyRows.slice(start, start + MAX_TABLE_ROWS_PER_SLIDE);
    const slideTitle = index === 0 ? title : `${title} (continued)`;
    const withTable = replaceBodyPlaceholderWithTable(
      renderTitledSlide(template, slideTitle),
      headers,
      chunk,
      template.fontFamily,
    );

    // Ensure a: namespace for table elements
    writeSlide(
      deck,
      slide,
      ensureSlideNamespace(withTable, "xmlns:a", DRAWINGML_NAMESPACE),
      template.rels,
    );
  }
}

/** An HTML artefact embedded as its pre-captured chart image. */
function addImageSlide(
  deck: DeckBuilder,
  template: ContentSlideTemplate,
  artefact: TemplateArtefact,
  image: PptxImageData,
): void {
  const slide = allocateSlide(deck);
  const imageMediaFilename = `chart_${artefact.id.replace(/[^a-zA-Z0-9]/g, "_")}.png`;
  const withImage = replaceBodyPlaceholderWithImage(
    renderTitledSlide(template, artefact.title),
    IMAGE_REL_ID,
    image.width,
    image.height,
  );

  // Store image binary in the zip
  deck.zip.file(`ppt/media/${imageMediaFilename}`, image.imageData);

  // Build slide rels with image reference appended
  const slideRels = (template.rels || EMPTY_SLIDE_RELS).replace(
    "</Relationships>",
    `<Relationship Id="${IMAGE_REL_ID}" Type="${IMAGE_RELATIONSHIP_TYPE}" Target="../media/${imageMediaFilename}"/></Relationships>`,
  );

  // Ensure r: namespace is declared for image embedding
  writeSlide(
    deck,
    slide,
    ensureSlideNamespace(withImage, "xmlns:r", RELATIONSHIPS_NAMESPACE),
    slideRels,
  );
}

/**
 * HTML artefacts with images → embed as picture; TABLE artefacts → native
 * table; everything else, including a TABLE that does not parse → text.
 */
function addArtefactSlides(
  deck: DeckBuilder,
  template: ContentSlideTemplate,
  artefact: TemplateArtefact,
  imageMap: PptxTemplateData["imageMap"],
): void {
  if (artefact.type === ArtefactType.TABLE) {
    const { headers, rows } = parseMarkdownTable(artefact.content);

    if (headers.length > 0) {
      addTableSlides(deck, template, artefact.title, headers, rows);

      return;
    }
  } else if (artefact.type === ArtefactType.HTML) {
    // Read once and branch on the value, so the check and the use cannot drift apart.
    const image = imageMap?.get(artefact.id);

    if (image !== undefined) {
      addImageSlide(deck, template, artefact, image);

      return;
    }
  }

  addTextSlide(deck, template, artefact);
}

/**
 * Add new slide references to presentation.xml, inserted after the last
 * existing `<p:sldId>` element.
 */
function appendSlideIds(presentationXml: string, newSlides: SlideRef[], maxSldId: number): string {
  if (!presentationXml.match(LAST_SLIDE_ID_PATTERN)) return presentationXml;

  const firstNewId = maxSldId - newSlides.length + 1;
  const newSldIdEntries = newSlides
    .map((s, index) => `<p:sldId id="${firstNewId + index}" r:id="${s.relId}"/>`)
    .join("");

  return presentationXml.replace(/<\/p:sldIdLst>/, `${newSldIdEntries}</p:sldIdLst>`);
}

/**
 * Remove the original content slide template: its part, its rels, its
 * `<p:sldId>` entry and its presentation relationship. Returns the updated
 * presentation.xml.
 */
function removeContentSlideTemplate(
  deck: DeckBuilder,
  presentationXml: string,
  contentSlide: { file: string; relsFile: string; hasRels: boolean; relId: string | undefined },
): string {
  deck.zip.remove(contentSlide.file);

  if (contentSlide.hasRels) {
    deck.zip.remove(contentSlide.relsFile);
  }

  if (!contentSlide.relId) return presentationXml;

  // Remove relationship entry
  deck.presentationRels = deck.presentationRels.replace(
    new RegExp(`<Relationship[^>]*Id="${contentSlide.relId}"[^>]*/>`),
    "",
  );

  // Remove <p:sldId> entry
  return presentationXml.replace(
    new RegExp(`<p:sldId[^>]*r:id="${contentSlide.relId}"[^>]*/>\\s*`),
    "",
  );
}

/**
 * Apply a PowerPoint template to artefact data.
 *
 * Strategy:
 * 1. Open template as ZIP
 * 2. Identify the title slide and content slide
 * 3. Replace placeholders in the title slide ({{TITLE}}, {{DATE}}, etc.)
 * 4. For each artefact, clone the content slide and inject content
 * 5. Update presentation.xml and [Content_Types].xml
 * 6. Output as Blob
 *
 * @param templateBuffer - The .pptx template file as ArrayBuffer
 * @param data - Template data with artefacts to inject
 * @returns Blob of the rendered .pptx presentation
 */
export async function applyPptxTemplate(
  templateBuffer: ArrayBuffer,
  data: PptxTemplateData,
): Promise<Blob> {
  try {
    const zip = new PizZip(templateBuffer);

    // Read presentation structure
    const presentationXml = zip.file("ppt/presentation.xml")?.asText();
    const presentationRels = zip.file("ppt/_rels/presentation.xml.rels")?.asText();

    if (!presentationXml || !presentationRels) {
      throw new Error("Invalid PPTX: missing presentation.xml or relationships");
    }

    const { slideRelIds, slideFilenames } = readSlideFilenames(presentationXml, presentationRels);

    if (slideFilenames.length === 0) {
      throw new Error("Invalid PPTX: no slides found");
    }

    fillTitleSlide(zip, `ppt/${slideFilenames[0]}`, data);

    // Identify the content slide template
    const contentSlideIdx = findContentSlideIndex(slideFilenames.length);
    const contentSlideFile = `ppt/${slideFilenames[contentSlideIdx]}`;
    const contentSlideXml = zip.file(contentSlideFile)?.asText();
    const contentSlideRelsFile = `ppt/slides/_rels/${slideFilenames[contentSlideIdx].replace("slides/", "")}.rels`;
    const contentSlideRels = zip.file(contentSlideRelsFile)?.asText();

    if (!contentSlideXml) {
      throw new Error("Cannot read content slide template");
    }

    const template: ContentSlideTemplate = {
      xml: contentSlideXml,
      rels: contentSlideRels,
      fontFamily: data.fontFamily,
      footerReplacements: buildFooterReplacements(data),
    };

    const deck: DeckBuilder = {
      zip,
      contentTypesXml: toPresentationContentTypes(zip.file("[Content_Types].xml")?.asText() || ""),
      presentationRels,
      maxSlideNum: getMaxSlideNumber(zip),
      maxRelId: getMaxRelId(presentationRels),
      maxSldId: getMaxSlideId(presentationXml),
      newSlides: [],
    };

    if (data.imageMap?.size) {
      deck.contentTypesXml = ensurePngContentType(deck.contentTypesXml);
    }

    // Clone the content slide for each artefact
    for (const artefact of data.artefacts) {
      addArtefactSlides(deck, template, artefact, data.imageMap);
    }

    let updatedPresentationXml = appendSlideIds(presentationXml, deck.newSlides, deck.maxSldId);

    // Optionally remove the original content slide template
    // (only if it's different from the title slide and we have artefacts)
    if (data.artefacts.length > 0 && contentSlideIdx > 0) {
      updatedPresentationXml = removeContentSlideTemplate(deck, updatedPresentationXml, {
        file: contentSlideFile,
        relsFile: contentSlideRelsFile,
        hasRels: Boolean(contentSlideRels),
        relId: slideRelIds[contentSlideIdx],
      });
    }

    // Write updated XML files back to ZIP
    zip.file("ppt/presentation.xml", updatedPresentationXml);
    zip.file("ppt/_rels/presentation.xml.rels", deck.presentationRels);
    zip.file("[Content_Types].xml", deck.contentTypesXml);

    const output = zip.generate({
      type: "blob",
      mimeType: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    });

    logger.info("PPTX template applied successfully", {
      artefactCount: data.artefacts.length,
      title: data.title,
    });

    return output;
  } catch (error) {
    logger.error("Failed to apply PPTX template", { error: String(error) });
    throw new Error(
      `Failed to apply PPTX template: ${error instanceof Error ? error.message : String(error)}`,
      { cause: error },
    );
  }
}

/**
 * Prepare PPTX template data from artefacts.
 */
export function preparePptxTemplateData(
  artefacts: Artefact[],
  options: {
    title?: string;
    author?: string;
    brand?: string;
    locale?: string;
    fontFamily?: string;
    imageMap?: Map<string, PptxImageData>;
  } = {},
): PptxTemplateData {
  return {
    title: options.title || "",
    // The locale parameter had never been passed by any caller, so this silently
    // followed navigator.language and rendered "August 28, 2026" on a US machine.
    // formatLongDate honours a day-first locale and corrects a month-first one.
    date: formatLongDate(new Date(), options.locale) ?? "",
    author: options.author || "",
    brand: options.brand || "",
    fontFamily: options.fontFamily,
    artefacts: artefacts.map((a) => ({
      id: a.id,
      title: a.title,
      content: a.content,
      type: a.type,
    })),
    imageMap: options.imageMap,
  };
}
